import RenderPipeline from "../renderpipeline";
import ShaderService from "../../shader/shader_service";
import ResourceService from "../../resource/resourceservice";
import Camera from "../../scene/camera";
import Mesh from "../../geometry/mesh";
import MeshData from "../../geometry/meshdata";
import Transform from "../transform";
import DioramaCamera from "../../scene/diorama_camera";


export default class DioramaRenderPipelineInstanced extends RenderPipeline {
  label: string;
  shader_name: string;
  texture_name: string;
  width: number;
  height: number;
  transforms: Transform[];

  camera_buffer: GPUBuffer | null = null;
  bind_group: GPUBindGroup | null = null;
  texture_bind_group: GPUBindGroup | null = null;
  instance_buffer: GPUBuffer | null = null;
  instance_count: number = 0;
  mesh: Mesh;
  placing:boolean = false;

  constructor(
    key: string,
    shader_name: string,
    texture_name: string,
    width: number,
    height: number,
    transforms: Transform[]
  ) {
    super(key + "_diorama_instanced");
    this.label = key + "_diorama_instanced";
    this.shader_name = shader_name;
    this.texture_name = texture_name + ".png";
    this.width = width;
    this.height = height;
    this.transforms = transforms;
    this.instance_count = transforms.length;
    this.mesh = new Mesh();
  }

  async build(
    device: GPUDevice,
    format: GPUTextureFormat,
    shader_service: ShaderService,
    resource_service: ResourceService,
  ): Promise<void> {
    const shader = shader_service.get_render(this.shader_name);

    this.pipeline = device.createRenderPipeline({
      layout: "auto",
      vertex: {
        module: shader,
        entryPoint: "vs_main",
        buffers: [
          {
            arrayStride: 32,
            stepMode: "vertex",
            attributes: [
              { shaderLocation: 0, offset: 0, format: "float32x3" },
              { shaderLocation: 1, offset: 12, format: "float32x3" },
              { shaderLocation: 2, offset: 24, format: "float32x2" },
            ],
          },
          {
            arrayStride: 64,
            stepMode: "instance",
            attributes: [
              { shaderLocation: 3, offset: 0, format: "float32x4" },
              { shaderLocation: 4, offset: 16, format: "float32x4" },
              { shaderLocation: 5, offset: 32, format: "float32x4" },
              { shaderLocation: 6, offset: 48, format: "float32x4" },
            ],
          },
        ],
      },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format,
            blend: {
              color: {
                operation: "add",
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
              },
              alpha: {
                operation: "add",
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
              },
            },
          },
        ],
      },
      primitive: {
        topology: "triangle-list",
        cullMode: "none",
      },
      depthStencil: {
        format: "depth24plus",
        depthWriteEnabled: true,
        depthCompare: "less",
      },
    });

    this.camera_buffer = device.createBuffer({
      size: 64,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      label: this.label,
      entries: [
        { binding: 0, resource: { buffer: this.camera_buffer } },
      ],
    });

    const texture_resource = await resource_service.getTexture(this.texture_name);
    const texture_asset = texture_resource.value;

    this.texture_bind_group = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(1),
      label: this.label + "_tex_bindg",
      entries: [
        { binding: 0, resource: texture_asset.sampler },
        { binding: 1, resource: texture_asset.view },
      ],
    });

    this.mesh.build(
      device,
      MeshData.make_bottom_anchored_vertical_plane(this.width, this.height)
    );

    const instance_data = new Float32Array(this.instance_count * 16);
    for (let i = 0; i < this.transforms.length; i++) {
      this.transforms[i].update_matrix();
      instance_data.set(this.transforms[i].model_matrix, i * 16);
    }

    this.instance_buffer = device.createBuffer({
      size: instance_data.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });

    device.queue.writeBuffer(
      this.instance_buffer,
      0,
      instance_data.buffer,
      instance_data.byteOffset,
      instance_data.byteLength
    );
  }

  set_diorama_pose(
      x: number,
      y: number,
      z: number,
      transform:Transform,
      camera: DioramaCamera
    ) {
        transform.set_position(x, y, z);
        transform.set_rotation(0, camera.get_shared_panel_yaw(), 0);
        transform.update_matrix();
    }

  prepare(device: GPUDevice, camera: Camera): void {
    if (!this.camera_buffer || !this.instance_buffer) return;

    device.queue.writeBuffer(
      this.camera_buffer,
      0,
      camera.view_projection_matrix.buffer as ArrayBuffer,
      camera.view_projection_matrix.byteOffset,
      camera.view_projection_matrix.byteLength
    );

    if(this.placing)
    {
        const instance_data = new Float32Array(this.instance_count * 16);
        const dc = camera as DioramaCamera;
        const shared_yaw = dc.get_shared_panel_yaw();
        for (let i = 0; i < this.transforms.length; i++) 
          {
          //this.transforms[i].set_rotation(0, shared_yaw, 0);
          //this.transforms[i].update_matrix();
          instance_data.set(this.transforms[i].model_matrix, i * 16);
        }

        device.queue.writeBuffer(
          this.instance_buffer,
          0,
          instance_data.buffer,
          instance_data.byteOffset,
          instance_data.byteLength
        );
    }
  }

  draw(pass: GPURenderPassEncoder): void {
    if (!this.pipeline || !this.bind_group || !this.texture_bind_group || !this.instance_buffer) {
      return;
    }

    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind_group);
    pass.setBindGroup(1, this.texture_bind_group);
    pass.setVertexBuffer(1, this.instance_buffer);
    this.mesh.draw_instanced(pass, this.instance_count);
  }
}